import Axios from 'axios';

import { REDUX_INSTALLER_CONFIG } from 'constants/persistence';
import { InstallerAppError } from 'services/InstallerApp/installerError';
import { proxy } from 'services/shared';

const {
  REACT_APP_PROXY_PORT,
  REACT_APP_PROXY_URL,
  REACT_APP_INSTALLER_URL,
  REACT_APP_INSTALLER_PORT,
} = process.env;

const axios = Axios.create({ timeout: 5 * 1000 });

const baseURL = () => {
  const { port } = JSON.parse(
    localStorage.getItem(REDUX_INSTALLER_CONFIG) || '{"port":"7777"}',
  );
  const baseURL = `https://localhost.erply.com:${port || 7777}/api/v1`;
  return baseURL;
};

const parseResponse = promise => {
  return promise
    .then(({ data }) => {
      //  TODO: fix once response form server is adjusted
      //  currently some errors have httpCode and Status differing
      //  once ths is fixed adjust this handler accordingly

      if (data.httpCode !== 200 || data.status !== 'OK') {
        throw new InstallerAppError(data);
      }
      return data;
    })
    .catch(err => {
      throw err;
    });
};

/**
 * Updates to latest version of Installer App
 * @returns {*}
 */
export const updateInstaller = () =>
  parseResponse(axios.get(`${baseURL()}/update`, { timeout: 60 * 1000 }));

/**
 * Return Installer App information
 */
export const getInfo = () => parseResponse(axios.get(`${baseURL()}/info`));

/**
 * Returns list of all available integrations in the cloud
 */
export const getAvailableIntegrations = () =>
  parseResponse(axios.get(`${baseURL()}/list`));

/**
 * Returns all available version for specified integration
 *
 * @param {String} entity - integration name
 */
export const getIntegrationVersion = entity =>
  parseResponse(axios.post(`${baseURL()}/list`, { entity }));

/**
 * Downloads the specified integration version
 *
 * @param {String} entity - integration name
 * @param {String} version - semantic version of the integration exmpl. "1.0.0"
 */
export const downloadIntegration = (entity, version) =>
  parseResponse(axios.post(`${baseURL()}/download`, { entity, version }, { timeout: 60 * 1000 }));

/**
 * Installs integration version, only if it has already been downloaded
 *
 * @param {String} entity - integration name
 * @param {String} version - semantic version of the integration exmpl. "1.0.0"
 */
export const installIntegration = (entity, version) =>
  parseResponse(axios.post(`${baseURL()}/install`, { entity, version }, { timeout: 60 * 1000 }));

/**
 * Removes specified integration version
 *
 * @param {String} entity - integration name
 * @param {String} version - semantic version of the integration exmpl. "1.0.0"
 */
export const removeIntegration = (entity, version) =>
  parseResponse(axios.post(`${baseURL()}/remove`, { entity, version }));

/**
 * Starts integration version, only if it has already been downloaded and installed
 *
 * @param {String} entity - integration name
 * @param {String} version - semantic version of the integration exmpl. "1.0.0"
 */
export const startIntegrationProcess = (entity, version) =>
  parseResponse(axios.post(`${baseURL()}/start`, { entity, version }));

/**
 * Starts integration version, only if it has already started
 *
 * @param {String} entity - integration name
 * @param {String} version - semantic version of the integration exmpl. "1.0.0"
 */
export const stopIntegrationProcess = (entity, version) =>
  parseResponse(axios.post(`${baseURL()}/stop`, { entity, version }));

/**
 * Starts integration version, only if it has already started
 *
 * @param {String} entity - integration name
 * @param {String} version - semantic version of the integration exmpl. "1.0.0"
 */
export const getIntegrationStatus = (entity, version) =>
  parseResponse(axios.post(`${baseURL()}/status`, { entity, version }));

/**
 * Starts integration version, only if it has already started
 *
 * @param {String} entity - integration name
 * @param {String} version - semantic version of the integration exmpl. "1.0.0"
 */
export const getIntegrationConfigValue = (entity, version) =>
  parseResponse(axios.post(`${baseURL()}/config/get`, { entity, version }));

/**
 * Starts integration version, only if it has already started
 *
 * @param {String} entity - integration name
 * @param {String} version - semantic version of the integration exmpl. "1.0.0"
 * @param {String} config - stringified JSON with the integration's configuration to be stored
 */
export const saveIntegrationConfig = (entity, version, config) =>
  parseResponse(
    axios.post(`${baseURL()}/config/save`, { entity, version, config }),
  );

/**
 * Starts integration version, only if it has already started
 *
 * @param {String} entity - integration name
 * @param {String} version - semantic version of the integration exmpl. "1.0.0"
 */
export const getIntegrationReadme = (entity, version) =>
  parseResponse(axios.post(`${baseURL()}/config/save`, { entity, version }));

/**
 * Mock Installer response function for testing purposes
 *
 * @param {{message: string, data: any}} responseBody - response message and data
 * @param {String} errorMessage - error message to be returned
 *
 * @returns Returns `success response` if only first parameter is passed. Returns `error response` if error message is passed as second parameter
 */
export const mockInstallerResponse = (responseBody = {}, errorMessage = null) =>
  errorMessage
    ? {
        httpCode: 500,
        dateTime: '20191111150612',
        status: 'FAIL',
        message: errorMessage,
      }
    : {
        httpCode: 200,
        dateTime: '20191111150612',
        status: 'OK',
        message: responseBody.message,
        data: responseBody.data,
      };

export const testUrl = () =>
  `${REACT_APP_PROXY_URL}:${REACT_APP_PROXY_PORT}/${REACT_APP_INSTALLER_URL}:${REACT_APP_INSTALLER_PORT}/api/v1/`;
